import { useCallback, useMemo } from 'react';
import Icon from '../../../../components/common/Icon/Icon';
import { IconKeys } from '../../../../components/common/Icon/IconKeys';
import BroadcastQueueCheckbox from './BroadcastQueueCheckbox';
import DeviceStatusCell from './DeviceStatusCell';
import S from './BroadcastQueueStyles';
import type { DeviceRow } from './types';
import { QUEUED_TAB_ID, statusToTabId } from './constants';

type Props = {
  fleetId?: string | null;
  rows: DeviceRow[];
  showCheckbox?: boolean;
  showCheckboxOnlyForQueued?: boolean;
  selectedDeviceIds?: string[];
  onSelectionChange?: (deviceId: string, checked: boolean) => void;
};

export default function DeviceTable({
  fleetId = null,
  rows,
  showCheckbox = false,
  showCheckboxOnlyForQueued = false,
  selectedDeviceIds = [],
  onSelectionChange,
}: Props): JSX.Element {
  const isSelected = useCallback(
    (deviceId: string): boolean => selectedDeviceIds.includes(deviceId),
    [selectedDeviceIds],
  );

  const rowsWithCheckbox = useMemo((): DeviceRow[] => {
    if (!showCheckboxOnlyForQueued) return rows;
    return rows.filter((r) => statusToTabId(r.messageStatus) === QUEUED_TAB_ID);
  }, [rows, showCheckboxOnlyForQueued]);

  const isAllSelected = rowsWithCheckbox.length > 0
    && rowsWithCheckbox.every((r) => isSelected(r.deviceId));

  const handleSelectAll = useCallback((): void => {
    if (!onSelectionChange) return;
    const newChecked = !isAllSelected;
    rowsWithCheckbox.forEach((r) => onSelectionChange(r.deviceId, newChecked));
  }, [onSelectionChange, isAllSelected, rowsWithCheckbox]);

  const isRowQueued = useCallback(
    (row: DeviceRow): boolean => statusToTabId(row.messageStatus) === QUEUED_TAB_ID,
    [],
  );

  const handleRowChange = useCallback(
    (deviceId: string, checked: boolean): void => {
      onSelectionChange?.(deviceId, checked);
    },
    [onSelectionChange],
  );

  if (rows.length === 0) {
    return (
      <S.TableWrap>
        <S.TableEmptyMessage>No data</S.TableEmptyMessage>
      </S.TableWrap>
    );
  }

  return (
    <S.TableWrap>
      <S.Table>
        <S.TableHead>
          <tr>
            <th>DEVICE ID</th>
            <th>MESSAGE STATUS</th>
            <th>DEVICE STATUS</th>
            {showCheckbox && (
              <S.CheckboxCellHead>
                <BroadcastQueueCheckbox
                  name="select-all"
                  state={isAllSelected}
                  onChange={(): void => {
                    handleSelectAll();
                  }}
                />
              </S.CheckboxCellHead>
            )}
          </tr>
        </S.TableHead>
        <S.TableBody>
          {rows.map((row) => (
            <tr key={row.id}>
              <S.DeviceIdCell>
                <Icon iconKey={IconKeys.PHOTO} />
                <span>{row.deviceId}</span>
              </S.DeviceIdCell>
              <S.MessageStatusCell>{row.messageStatus ? row.messageStatus.charAt(0).toUpperCase() + row.messageStatus.slice(1) : '-'}</S.MessageStatusCell>
              {fleetId ? (
                <DeviceStatusCell
                  fleetId={fleetId}
                  deviceId={row.deviceId}
                  fallback={row.deviceStatus}
                />
              ) : (
                <S.DeviceStatusCell $isOffline={row.deviceStatus?.toLowerCase() === 'offline'}>
                  {row.deviceStatus}
                </S.DeviceStatusCell>
              )}
              {showCheckbox && (
                <S.CheckboxCell>
                  {(!showCheckboxOnlyForQueued || isRowQueued(row)) && (
                    <BroadcastQueueCheckbox
                      name={row.deviceId}
                      state={isSelected(row.deviceId)}
                      onChange={handleRowChange}
                    />
                  )}
                </S.CheckboxCell>
              )}
            </tr>
          ))}
        </S.TableBody>
      </S.Table>
    </S.TableWrap>
  );
}
